import { useEdgeInfiniteQuery } from '@app/data/edge/hooks';
import useCountry from '@drc/hooks/useCountry';
import { useCurrency } from '@app/currency';
import { useCustomer } from '@app/user';
import { InfiniteData } from '@tanstack/react-query';
import { GET_ALL_FAVORITES_LIST } from '../queries';
import {
  GetAllFavoritesListQuery,
  GetAllFavoritesListQueryVariables,
} from '../queries/__generated__/GetAllFavoritesList.types';

const selectAllFavoriteListsData = (
  data: InfiniteData<GetAllFavoritesListQuery>,
) => ({
  id: '',
  public: true,
  pages: data?.pages,
  pageParams: data?.pageParams,
  products:
    data?.pages
      ?.map((page) =>
        page?.lists?.edges?.[0]?.node?.items?.edges?.map((t) => t?.node),
      )
      .flat() ?? [],
  listName: data?.pages?.[0]?.lists?.edges?.[0]?.node?.name,
  totalProducts:
    data?.pages?.[0]?.lists?.edges?.[0]?.node?.items?.pageInfo?.total,
});

type UseAllFavoritesListReturnType = ReturnType<
  typeof selectAllFavoriteListsData
>;

interface UseAllFavoritesListQueryData
  extends InfiniteData<GetAllFavoritesListQuery> {
  id?: string;
  public?: boolean;
  pages: Record<string, any>[];
  pageParams: number[] | unknown[];
  products?: Record<string, any>[];
  listName?: string;
  totalProducts?: number;
}

interface UseAllFavoritesListReturnObject {
  data: UseAllFavoritesListQueryData | undefined;
  isLoading: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  refetch: () => void;
}

// Fetches a user's 'All Favorites' list
const useAllFavoritesList = (): UseAllFavoritesListReturnObject => {
  const countryCode = useCountry();
  const customer = useCustomer();
  const { currency: currencyCode } = useCurrency();
  return useEdgeInfiniteQuery<
    GetAllFavoritesListQuery,
    GetAllFavoritesListQueryVariables,
    UseAllFavoritesListReturnType
  >(GET_ALL_FAVORITES_LIST, {
    variables: {
      currencyCode,
      countryCode,
      market: customer?.marketCountry,
    },
    getNextPageParam: (lastPage) => {
      const pageInfo = lastPage?.lists?.edges[0]?.node?.items?.pageInfo;
      if (!pageInfo) {
        return null;
      }

      return pageInfo.index + 1;
    },
    formatContext: (queryFunctionContext) => {
      const nextIndex = queryFunctionContext.pageParam;
      const index = queryFunctionContext.pageParam ? nextIndex : 1;
      queryFunctionContext.queryKey[1].variables.page = {
        index,
        limit: 15,
      };
      return queryFunctionContext;
    },
    select: (data: InfiniteData<GetAllFavoritesListQuery>) =>
      selectAllFavoriteListsData(data),
    cacheTime: 0,
  });
};

export default useAllFavoritesList;
